/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Button from '@ba-ui-toolkit/button';
import ToolTip from '@ba-ui-toolkit/tooltip';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import Container from '@ba-ui-toolkit/container';
import ContextMenu from '@ba-ui-toolkit/contextmenu';
import { sortTypes } from '@ba-ui-toolkit/constants';
import arrowsVertical16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/arrows--vertical_16';
import arrowDown16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/arrow--down_16';
import arrowUp16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/arrow--up_16';
import ProgressIndicator from '@ba-ui-toolkit/progressindicator';
import './GridSortCell.scss';
import stringResource from './nls/StringResource';
// TODO Add NLS
// import { getString } from '../../nls/StringResources';

function getString(value) {
	return stringResource.get(value);
}

const { sanitize } = propsUtils;
const { DESC, ASC, NO_SORT } = sortTypes;
const MENU_NAME = 'gridSortCellMenu';

export default class GridSortCell extends Component {
	static propTypes = {
		closeSortMenu: PropTypes.bool,
		sortType: PropTypes.string.isRequired,
		priority: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		onSortChange: PropTypes.func,
		className: PropTypes.string,
		getLocaleString: PropTypes.func,
		style: PropTypes.object,
		col: PropTypes.number,
		isSorting: PropTypes.bool,
		sortedCol: PropTypes.number,
		onSortMenu: PropTypes.func,
	};

	static defaultProps = {
		className: '',
		style: {},
		isSorting: false,
		getLocaleString: getString,
	};

	menuContent = [
		{
			id: 'iasc',
			label: this.props.getLocaleString('ascending'),
			value: ASC,
		},
		{
			id: 'idsc',
			label: this.props.getLocaleString('descending'),
			value: DESC,
		},
		{
			id: 'insrt',
			label: this.props.getLocaleString('noSort'),
			value: NO_SORT,
		},
	];

	state = {
		open: false,
	};

	_onSelectionChange = (name, sortType) => {
		const { onSortChange } = this.props;
		this._toggleContextMenu(false);
		onSortChange && onSortChange(sortType);
	};

	_setSortIcon() {
		const { sortType } = this.props;

		if (sortType === DESC) {
			return arrowDown16.id;
		}
		if (sortType === ASC) {
			return arrowUp16.id;
		}

		return arrowsVertical16.id;
	}

	_sortBtnRef = r => {
		this._sortBtn = ReactDOM.findDOMNode(r);
	};

	_toggleContextMenu = open => {
		const { onSortMenu } = this.props;

		this.setState({ open });
		open && onSortMenu && onSortMenu();
	};

	_getSortKey() {
		const { sortType } = this.props;

		let sortKey;
		switch (sortType) {
			case '':
				sortKey = 'noSort';
				break;
			case 'asc':
				sortKey = 'ascending';
				break;
			case 'desc':
				sortKey = 'descending';
				break;
			default:
				sortKey = '';
		}

		return sortKey;
	}

	_isSortingUnderway() {
		const { isSorting, sortedCol, col } = this.props;
		if (isSorting && sortedCol === col) {
			return true;
		}

		return false;
	}

	_renderSpinner() {
		return (
			<ProgressIndicator
				size="normal"
				variant="circle"
				style={{ paddingRight: '5px', paddingLeft: '5px' }}
			/>
		);
	}

	render() {
		const { open } = this.state;
		const {
			className,
			style,
			priority,
			sortType,
			getLocaleString,
			isSorting,
			sortedCol,
			col,
			...others
		} = this.props;
		const classNames = cx('ba-common-grid__sort-cell');

		const sortTitle = this._getSortKey();

		return (
			<Container
				{...sanitize(others, GridSortCell)}
				className={`${classNames} ${className}`}
				style={style}
			>
				<div className="ba-common-grid__sort-cell-flexdiv">
					<div className="ba-common-grid__sort-cell-text" />
					<div className="ba-common-grid__sort-cell-order-icon">
						{!this._isSortingUnderway() && (
							<div className="ba-common-grid__sort-cell-priority">
								{priority === 0 ? '' : priority}
							</div>
						)}
						<span
							ref={r => {
								this._DOMNodeToAttachTo = r;
							}}
						>
							{this._isSortingUnderway() ? (
								this._renderSpinner()
							) : (
								<ToolTip orient="left" title={getLocaleString(sortTitle)}>
									<Button
										className={cx('ba-common-grid__sort-cell-icons', {
											sort: sortType && sortType.length > 0,
										})}
										icon={this._setSortIcon()}
										iconSize="small"
										variant="icon"
										ref={this._sortBtnRef}
										onClick={() => this._toggleContextMenu(true)}
										disabled={isSorting}
									/>
								</ToolTip>
							)}
						</span>
					</div>
				</div>
				{open && (
					<ContextMenu
						placement="bottomRight"
						triggerNode={this._sortBtn}
						domNodeToAttachTo={document.body}
						onClose={() => this._toggleContextMenu(false)}
						onChange={this._onSelectionChange}
						selected={{
							[MENU_NAME]: sortType,
						}}
					>
						<ContextMenu.List content={this.menuContent} name={MENU_NAME} />
					</ContextMenu>
				)}
			</Container>
		);
	}
}
